import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Typography,
    Alert,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import { CloudUpload, Download, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { Layout } from '../layout/Layout';
import { Order } from '../../types/order';
import { Client } from '../../types/client';
import { orderCreate, getCreateForm as getOrderCreateForm } from '../../api/orderService';
import { clientCreate, getCreateForm as getClientCreateForm } from '../../api/clientService';
import { useHeaders } from '../../hooks/useHeaders';
import { OrderForm } from '../../types/orderForm';
import { ClientForm } from '../../types/clientForm';
import { Country } from '../../types/country';
import { Municipality } from '../../types/municipality';

interface ImportResult {
    success: boolean;
    row: number;
    data: ParsedClientOrder;
    error?: string;
    clientCreated?: boolean;
    orderCreated?: boolean;
}

interface ParsedClientOrder {
    rowNumber: number;
    number?: string;
    fullName?: string;
    nationalId?: string;
    quantity_per_kg?: number;
    price?: number;
    total?: number;
    orderDate?: Date;
}

interface ImportProgress {
    current: number;
    total: number;
    currentItemName?: string;
}

export const UnifiedImport = () => {
    const [file, setFile] = useState<File | null>(null);
    const [parsedData, setParsedData] = useState<ParsedClientOrder[]>([]);
    const [importResults, setImportResults] = useState<ImportResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState<ImportProgress>({ current: 0, total: 0 });
    const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
    const [clientForm, setClientForm] = useState<ClientForm | null>(null);
    const [showResults, setShowResults] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const headers = useHeaders();

    // Get form data
    React.useEffect(() => {
        const fetchForms = async () => {
            try {
                const [orderFormData, clientFormData] = await Promise.all([
                    getOrderCreateForm(headers),
                    getClientCreateForm(headers)
                ]);
                setOrderForm(orderFormData);
                setClientForm(clientFormData);
            } catch (error) {
                console.error('Failed to fetch forms:', error);
            }
        };
        fetchForms();
    }, []); // Empty dependency array to run only once

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            parseExcelFile(selectedFile);
        }
    };

    const parseExcelDate = (dateValue: any): Date | undefined => {
        if (!dateValue) return undefined;
        
        // If it's already a Date object
        if (dateValue instanceof Date) {
            return isNaN(dateValue.getTime()) ? undefined : dateValue;
        }
        
        // If it's an Excel serial date number
        if (typeof dateValue === 'number') {
            // Excel stores dates as numbers (days since 1900-01-01)
            // But we need to check if it's a valid date number (not just any number)
            if (dateValue > 0 && dateValue < 1000000) {
                const excelDate = new Date((dateValue - 25569) * 86400 * 1000);
                if (!isNaN(excelDate.getTime())) {
                    return excelDate;
                }
            }
        }
        
        // If it's a string, try multiple parsing strategies
        if (typeof dateValue === 'string') {
            const trimmed = dateValue.trim();
            if (!trimmed) return undefined;
            
            // Check if it's an Excel serial date number as string (e.g., "45321")
            const numValue = parseFloat(trimmed);
            if (!isNaN(numValue) && numValue > 0 && numValue < 1000000 && trimmed.indexOf('.') === -1 && trimmed.indexOf('/') === -1) {
                // Might be an Excel serial date
                const excelDate = new Date((numValue - 25569) * 86400 * 1000);
                if (!isNaN(excelDate.getTime())) {
                    return excelDate;
                }
            }
            
            // Try European date formats FIRST (DD.MM.YYYY or DD/MM/YYYY) so e.g. 05.01.2025 = 5 Jan, not 1 May
            const ddMmYyyy = /^(\d{1,2})[\/\.](\d{1,2})[\/\.](\d{4})$/;
            const yyyyMmDd = /^(\d{4})[\/\.](\d{1,2})[\/\.](\d{1,2})$/;
            const ddMmMatch = trimmed.match(ddMmYyyy);
            if (ddMmMatch) {
                const day = parseInt(ddMmMatch[1], 10);
                const month = parseInt(ddMmMatch[2], 10) - 1;
                const year = parseInt(ddMmMatch[3], 10);
                const parsed = new Date(year, month, day);
                if (!isNaN(parsed.getTime()) && parsed.getFullYear() === year && parsed.getMonth() === month && parsed.getDate() === day) {
                    return parsed;
                }
            }
            const yyyyMmMatch = trimmed.match(yyyyMmDd);
            if (yyyyMmMatch) {
                const year = parseInt(yyyyMmMatch[1], 10);
                const month = parseInt(yyyyMmMatch[2], 10) - 1;
                const day = parseInt(yyyyMmMatch[3], 10);
                const parsed = new Date(year, month, day);
                if (!isNaN(parsed.getTime())) {
                    return parsed;
                }
            }
            
            // ISO format YYYY-MM-DD or standard Date parsing
            let parsed = new Date(trimmed);
            if (!isNaN(parsed.getTime())) {
                return parsed;
            }
        }
        
        return undefined;
    };

    const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
        const parts = fullName.trim().split(' ');
        if (parts.length === 1) {
            return { firstName: parts[0], lastName: '' };
        }
        const firstName = parts[0];
        const lastName = parts.slice(1).join(' ');
        return { firstName, lastName };
    };

    const parseExcelFile = async (file: File) => {
        setLoading(true);
        setErrors([]);
        setParsedData([]);

        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array', cellDates: true, cellNF: false, cellText: false });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, defval: null }) as any[][];

            if (jsonData.length < 2) {
                setErrors(['Skedari duhet të ketë të paktën një rresht me të dhëna']);
                setLoading(false);
                return;
            }

            // Expected headers for combined client + order data (single Quantity (kg) column)
            const expectedHeaders = [
                'Number', 'Full Name', 'National ID', 'Quantity (kg)', 'Price', 'Total', 'Order Date'
            ];

            const headers = jsonData[0];
            const validationErrors: string[] = [];

            // Validate headers
            expectedHeaders.forEach((expectedHeader, index) => {
                if (headers[index] !== expectedHeader) {
                    validationErrors.push(`Kolona ${index + 1} duhet të jetë "${expectedHeader}", por është "${headers[index] || 'e zbrazët'}"`);
                }
            });

            if (validationErrors.length > 0) {
                validationErrors.unshift('❌ Skedari ka format të gabuar! Shkarko template-in e saktë dhe përdore atë.');
                validationErrors.push('💡 Kjo është një importim i unifikuar: çdo rresht duhet të ketë të dhënat e klientit + porosinë e tij.');
                setErrors(validationErrors);
                setLoading(false);
                return;
            }

            // Parse data rows
            const parsed: ParsedClientOrder[] = [];
            const parseErrors: string[] = [];

            for (let i = 1; i < jsonData.length; i++) {
                const row = jsonData[i];
                const rowNumber = i + 1;

                if (row.every(cell => cell === null || cell === undefined || (typeof cell === 'string' && !cell.trim()))) continue; // Skip empty rows

                // Extract date value, handling null, undefined, and empty strings
                const dateValue = row[6];
                const dateStr = dateValue !== null && dateValue !== undefined ? String(dateValue).trim() : '';
                
                const clientOrder: ParsedClientOrder = {
                    rowNumber,
                    number: row[0] !== null && row[0] !== undefined ? String(row[0]).trim() : '',
                    fullName: row[1] !== null && row[1] !== undefined ? String(row[1]).trim() : '',
                    nationalId: row[2] !== null && row[2] !== undefined ? String(row[2]).trim() : '',
                    quantity_per_kg: row[3] !== null && row[3] !== undefined ? parseFloat(String(row[3])) || 0 : 0,
                    price: row[4] !== null && row[4] !== undefined ? parseFloat(String(row[4])) || 0 : 0,
                    total: row[5] !== null && row[5] !== undefined ? parseFloat(String(row[5])) || 0 : 0,
                    orderDate: dateStr ? parseExcelDate(dateValue) : undefined
                };

                // Validate required fields
                if (!clientOrder.number) parseErrors.push(`Rreshti ${rowNumber}: Numri është i detyrueshëm`);
                if (!clientOrder.fullName) parseErrors.push(`Rreshti ${rowNumber}: Emri i plotë është i detyrueshëm`);
                if (!clientOrder.nationalId) parseErrors.push(`Rreshti ${rowNumber}: Nr. Identitetit është i detyrueshëm`);
                if (!clientOrder.quantity_per_kg || clientOrder.quantity_per_kg <= 0) parseErrors.push(`Rreshti ${rowNumber}: Sasia (kg) duhet të jetë më e madhe se 0`);
                if (!clientOrder.price || clientOrder.price <= 0) parseErrors.push(`Rreshti ${rowNumber}: Çmimi duhet të jetë më i madh se 0`);
                if (!clientOrder.orderDate) parseErrors.push(`Rreshti ${rowNumber}: Data e porosisë është e detyrueshme`);

                parsed.push(clientOrder);
            }

            if (parseErrors.length > 0) {
                setErrors(parseErrors);
            } else {
                setParsedData(parsed);
            }
        } catch (error) {
            setErrors(['Gabim gjatë leximit të skedarit Excel']);
            console.error('Excel parsing error:', error);
        }

        setLoading(false);
    };

    const findExistingClient = (nationalId: string): Client | undefined => {
        if (!orderForm?.clients) return undefined;
        return orderForm.clients.find(client => client.nationalId === nationalId);
    };

    const handleImport = async () => {
        if (!parsedData.length || !orderForm || !clientForm) return;

        setImporting(true);
        const results: ImportResult[] = [];
        
        // Get current user
        const storedData = sessionStorage.getItem('auth');
        const sessionAuthData = storedData ? JSON.parse(storedData) : null;
        const user = sessionAuthData ? sessionAuthData.user : null;

        setImportProgress({ current: 0, total: parsedData.length });

        for (let i = 0; i < parsedData.length; i++) {
            const item = parsedData[i];
            
            setImportProgress({ 
                current: i + 1, 
                total: parsedData.length,
                currentItemName: item.fullName
            });

            let clientCreated = false;
            let orderCreated = false;
            let client: Client | undefined;

            try {
                // Check if client already exists
                client = findExistingClient(item.nationalId!);

                if (!client) {
                    // Create new client
                    const { firstName, lastName } = splitFullName(item.fullName!);
                    
                    // Use the first available country from the form data
                    const defaultCountry = clientForm.countries && clientForm.countries.length > 0 
                        ? clientForm.countries[0] 
                        : undefined;
                    
                    if (!defaultCountry) {
                        results.push({
                            success: false,
                            row: item.rowNumber,
                            data: item,
                            error: 'Nuk ka vende të disponueshme në sistem',
                            clientCreated: false,
                            orderCreated: false
                        });
                        continue;
                    }
                    
                    const clientData: Client = {
                        firstName,
                        lastName,
                        nationalId: item.nationalId,
                        idCountry: defaultCountry, // Use the first available country
                        idUser: user,
                        isImported: true, // Always set to true for imported clients
                        // Set other fields to undefined as specified
                        email: undefined,
                        phone: undefined,
                        address: undefined,
                        place: undefined,
                        digitalSignature: null,
                        idMunicipality: undefined
                    };

                    const clientResult = await clientCreate(clientData, headers);
                    
                    // Extract the client object from the API response
                    client = clientResult.client;
                    clientCreated = !clientResult.isExisting; // True if new, false if existing
                    
                    if (!client || !client.idClient) {
                        results.push({
                            success: false,
                            row: item.rowNumber,
                            data: item,
                            error: 'Klienti nuk u krijua ose nuk u gjet',
                            clientCreated: false,
                            orderCreated: false
                        });
                        continue;
                    }
                }

                // Create order for the client
                let orderNumber: number | undefined = undefined;
                if (item.number && item.number.toString().trim() !== '') {
                    const parsed = parseInt(item.number.toString().trim(), 10);
                    if (!isNaN(parsed)) {
                        orderNumber = parsed;
                    }
                }
            
                
                if (!client || !client.idClient) {
                    results.push({
                        success: false,
                        row: item.rowNumber,
                        data: item,
                        error: 'Klienti nuk ka ID të vlefshëm',
                        clientCreated,
                        orderCreated: false
                    });
                    continue;
                }
                
                const orderData: Order = {
                    idClient: client,
                    orderNumber: orderNumber,
                    quantity_per_kg: item.quantity_per_kg,
                    price: item.price,
                    orderDate: item.orderDate,
                    type: 'Katalizator mbeturinë', // Default type
                    totalPrice: item.total,
                    idUser: user
                };

                console.log('Creating order for row', item.rowNumber, 'with data:', orderData);
                console.log('Order data orderNumber specifically:', orderData.orderNumber);

                try {
                    await orderCreate(orderData, headers);
                    orderCreated = true;
                } catch (orderError) {
                    console.error('Order creation error for row', item.rowNumber, ':', orderError);
                    results.push({
                        success: false,
                        row: item.rowNumber,
                        data: item,
                        error: `Gabim në krijimin e porosisë: ${orderError instanceof Error ? orderError.message : 'Gabim i panjohur'}`,
                        clientCreated,
                        orderCreated: false
                    });
                    continue;
                }

                results.push({
                    success: true,
                    row: item.rowNumber,
                    data: item,
                    clientCreated,
                    orderCreated
                });

            } catch (error) {
                results.push({
                    success: false,
                    row: item.rowNumber,
                    data: item,
                    error: error instanceof Error ? error.message : 'Gabim i panjohur',
                    clientCreated,
                    orderCreated: false
                });
            }
        }

        setImportResults(results);
        setImporting(false);
        setShowResults(true);
    };

    const downloadTemplate = () => {
        const template = [
            ['Number', 'Full Name', 'National ID', 'Quantity (kg)', 'Price', 'Total', 'Order Date'],
            ['1', 'Agron Mustafa', '1234567890', '10', '5.50', '55.00', '2024-01-15'],
            ['2', 'Fatmir Krasniqi', '0987654321', '5', '10.00', '50.00', '2024-01-16'],
            ['', '', '', '', '', '', ''],
            ['SHENIM: Fshini rreshtat me shembuj dhe zëvendësoni me të dhënat tuaja', '', '', '', '', '', ''],
            ['SHENIM: Numri do të përdoret si numër porosie', '', '', '', '', '', ''],
            ['SHENIM: Emri i plotë do të ndahet në emër dhe mbiemër', '', '', '', '', '', ''],
            ['SHENIM: Data duhet të jetë në formatin YYYY-MM-DD', '', '', '', '', '', '']
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(template);
        
        // Set column widths
        worksheet['!cols'] = [
            { width: 15 }, // ID
            { width: 20 }, // Full Name
            { width: 15 }, // National ID
            { width: 14 }, // Quantity (kg)
            { width: 12 }, // Price
            { width: 12 }, // Total
            { width: 15 }  // Order Date
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
        XLSX.writeFile(workbook, 'template_klient_porosi.xlsx');
    };

    const downloadFailedRows = () => {
        const failedResults = importResults.filter(r => !r.success);
        
        if (failedResults.length === 0) {
            return;
        }

        // Create header row
        const header = ['Number', 'Full Name', 'National ID', 'Quantity (kg)', 'Price', 'Total', 'Order Date'];
        
        // Helper function to format date
        const formatDate = (date: Date | undefined): string => {
            if (!date) return '';
            if (date instanceof Date && !isNaN(date.getTime())) {
                return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
            }
            return '';
        };

        // Convert failed rows to Excel format
        const data = failedResults.map(result => {
            const item = result.data;
            return [
                item.number || '',
                item.fullName || '',
                item.nationalId || '',
                item.quantity_per_kg?.toString() || '',
                item.price?.toString() || '',
                item.total?.toString() || '',
                formatDate(item.orderDate)
            ];
        });

        // Combine header and data
        const excelData = [header, ...data];

        const worksheet = XLSX.utils.aoa_to_sheet(excelData);
        
        // Set column widths
        worksheet['!cols'] = [
            { width: 15 }, // Number
            { width: 20 }, // Full Name
            { width: 15 }, // National ID
            { width: 14 }, // Quantity (kg)
            { width: 12 }, // Price
            { width: 12 }, // Total
            { width: 15 }  // Order Date
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Failed Rows');
        XLSX.writeFile(workbook, `failed_rows_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const resetImport = () => {
        setFile(null);
        setParsedData([]);
        setImportResults([]);
        setErrors([]);
        setShowResults(false);
        setImportProgress({ current: 0, total: 0 });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const successCount = importResults.filter(r => r.success).length;
    const errorCount = importResults.filter(r => !r.success).length;
    const newClientsCount = importResults.filter(r => r.clientCreated).length;
    const newOrdersCount = importResults.filter(r => r.orderCreated).length;

    return (
        <Layout>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    py: 8,
                    width: '90%',
                    margin: 'auto',
                    '@media screen and (max-width: 576px)': { width: '95%' }
                }}
            >
                <Card>
                    <CardHeader
                        title="Importo Klientët dhe Porositë nga Excel"
                        subheader="Ngarko një skedar Excel me të dhënat e klientëve dhe porositë e tyre për t'i importuar në sistem"
                    />
                    <CardContent>
                        {/* Template Download */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                1. Shkarko Template
                            </Typography>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <strong>E rëndësishme:</strong> Duhet të përdorni template-in e saktë! Ky është import i unifikuar - çdo rresht përmban të dhënat e klientit DHE porosinë e tij në të njëjtin rresht.
                            </Alert>
                            <Button
                                variant="outlined"
                                startIcon={<Download />}
                                onClick={downloadTemplate}
                                sx={{ mb: 2 }}
                            >
                                Shkarko Template Excel
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                                Shkarkoni template për të parë formatin e saktë. Çdo rresht përfshin të dhënat e klientit dhe porosinë e tij.
                            </Typography>
                        </Box>

                        {/* File Upload */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                2. Ngarko Skedarin Excel
                            </Typography>
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                            />
                            <Button
                                variant="contained"
                                startIcon={<CloudUpload />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={loading}
                            >
                                {file ? file.name : 'Zgjedh Skedarin Excel'}
                            </Button>
                            {loading && (
                                <Box sx={{ mt: 2 }}>
                                    <LinearProgress />
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Duke analizuar skedarin...
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Errors */}
                        {errors.length > 0 && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Gabime të gjetura:
                                </Typography>
                                <List dense>
                                    {errors.map((error, index) => (
                                        <ListItem key={index} sx={{ py: 0 }}>
                                            <ListItemText primary={error} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Alert>
                        )}

                        {/* Preview */}
                        {parsedData.length > 0 && (
                            <>
                                <Typography variant="h6" gutterBottom>
                                    3. Kontrollo të Dhënat
                                </Typography>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Gjetur {parsedData.length} rresht në skedar. Kontrollo të dhënat para se të importosh.
                                </Alert>
                                
                                <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 400 }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Rreshti</TableCell>
                                                <TableCell>Numri</TableCell>
                                                <TableCell>Emri i Plotë</TableCell>
                                                <TableCell>Nr. ID</TableCell>
                                                <TableCell>Sasia (kg)</TableCell>
                                                <TableCell>Çmimi</TableCell>
                                                <TableCell>Total</TableCell>
                                                <TableCell>Data</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {parsedData.map((item) => (
                                                <TableRow key={item.rowNumber}>
                                                    <TableCell>{item.rowNumber}</TableCell>
                                                    <TableCell>{item.number}</TableCell>
                                                    <TableCell>{item.fullName}</TableCell>
                                                    <TableCell>{item.nationalId}</TableCell>
                                                    <TableCell>{item.quantity_per_kg}</TableCell>
                                                    <TableCell>€{item.price?.toFixed(2)}</TableCell>
                                                    <TableCell>€{item.total?.toFixed(2)}</TableCell>
                                                    <TableCell>
                                                        {item.orderDate?.toLocaleDateString('sq-AL')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </CardContent>

                    {parsedData.length > 0 && (
                        <CardActions sx={{ justifyContent: 'space-between', p: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={resetImport}
                                disabled={importing}
                            >
                                Anulo
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleImport}
                                disabled={importing || errors.length > 0}
                            >
                                {importing ? 'Duke Importuar...' : `Importo ${parsedData.length} Rresht`}
                            </Button>
                        </CardActions>
                    )}

                    {importing && (
                        <Box sx={{ px: 3, pb: 3 }}>
                            <LinearProgress 
                                variant={importProgress.total > 0 ? "determinate" : "indeterminate"}
                                value={importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}
                            />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {importProgress.total > 0 ? (
                                    <>
                                        Duke importuar rreshtin {importProgress.current} nga {importProgress.total}
                                        {importProgress.currentItemName && (
                                            <> - {importProgress.currentItemName}</>
                                        )}
                                    </>
                                ) : (
                                    'Duke importuar të dhënat...'
                                )}
                            </Typography>
                        </Box>
                    )}
                </Card>

                {/* Results Dialog */}
                <Dialog 
                    open={showResults} 
                    onClose={() => setShowResults(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        Rezultatet e Importimit
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mb: 2 }}>
                            <Chip
                                icon={<CheckCircle />}
                                label={`${successCount} të suksesshme`}
                                color="success"
                                sx={{ mr: 1, mb: 1 }}
                            />
                            {errorCount > 0 && (
                                <Chip
                                    icon={<ErrorIcon />}
                                    label={`${errorCount} me gabime`}
                                    color="error"
                                    sx={{ mr: 1, mb: 1 }}
                                />
                            )}
                            <Chip
                                label={`${newClientsCount} klient të rinj`}
                                color="info"
                                sx={{ mr: 1, mb: 1 }}
                            />
                            <Chip
                                label={`${newOrdersCount} porosi të reja`}
                                color="primary"
                                sx={{ mb: 1 }}
                            />
                        </Box>

                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Rreshti</TableCell>
                                        <TableCell>Klienti</TableCell>
                                        <TableCell>Klient i Ri</TableCell>
                                        <TableCell>Porosi e Re</TableCell>
                                        <TableCell>Gabimi</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {importResults.map((result, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    icon={result.success ? <CheckCircle /> : <ErrorIcon />}
                                                    label={result.success ? 'OK' : 'Gabim'}
                                                    color={result.success ? 'success' : 'error'}
                                                />
                                            </TableCell>
                                            <TableCell>{result.row}</TableCell>
                                            <TableCell>
                                                {result.data.fullName}
                                            </TableCell>
                                            <TableCell>
                                                {result.clientCreated ? '✓' : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {result.orderCreated ? '✓' : '-'}
                                            </TableCell>
                                            <TableCell>{result.error || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowResults(false)}>
                            Mbyll
                        </Button>
                        {errorCount > 0 && (
                            <Button 
                                onClick={downloadFailedRows} 
                                variant="outlined"
                                startIcon={<Download />}
                                color="error"
                            >
                                Shkarko Rreshtat me Gabime ({errorCount})
                            </Button>
                        )}
                        <Button onClick={resetImport} variant="contained">
                            Importo Përsëri
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    );
};